'use client';

import { usePathname, useSearchParams } from 'next/navigation';
import { useEffect } from 'react';
import { create } from 'zustand';

import {
  DONATION_ROUTES,
  ENV,
  MODULE_ROUTES,
  ROUTE_TYPES,
  ROUTES,
  SEARCH_PARAMS,
  STORAGE_KEY,
} from '@/constants';
import { useAuthStore } from '@/stores/AuthStore';
import { useGlobalStore } from '@/stores/GlobalStore';
import { findRoute } from '@/utils';
import { findDonationRoute } from '@/utils/findDonationRoute';

import { moveBackInfo } from '../_utils/moveBackInfo';
import { isKbWebview } from '../_utils/nativeCall';
import { setTopMenuV2 } from '../_utils/setTopMenuV2';

export const KB_DUMMY_PATHNAME = 'kbpostparam';
export const KB_DUMMY_PATHNAME_SPLIT_POINT = '__';

interface KbHistoryStore {
  historyList: string[];
  setHistoryList: (setter: (history: string[]) => string[]) => void;
}

export const kbHistoryStore = create<KbHistoryStore>((set) => ({
  historyList: [],
  setHistoryList: (setter) =>
    set((state) => ({ historyList: setter(state.historyList) })),
}));

export const KbHistoryStack = () => {
  const searchParams = useSearchParams();
  const pathname = usePathname();
  const isLogin = useAuthStore((state) => state.isLogin);

  const setCurrentRouteType = useGlobalStore(
    (state) => state.setCurrentRouteType,
  );

  useEffect(() => {
    if (!pathname) return;

    if (!isKbWebview()) {
      const isAllowedPath = [
        '/login',
        '/join',
        '/terms',
        '/easy-certification',
        '/filter',
        '/donation',
      ].some((path) => pathname.startsWith(path));

      if (!isAllowedPath) {
        const originalWello = ENV.NEXT_PUBLIC_DOMAIN;

        location.href = location.href.replace(location.origin, originalWello);
      }
    }
  }, [pathname]);

  useEffect(() => {
    setTopMenuV2('', '', {
      menuBt: 'Y',
      preBt: 'Y',
      close: 'N',
      title: '맞춤형 정책지원금 알림',
      preAction: 'B',
      target: '',
    });
  }, []);

  const currentRoute = findRoute(pathname) ?? findDonationRoute(pathname);
  const isDonationRoute = pathname?.startsWith(DONATION_ROUTES.HOME.pathname);

  useEffect(() => {
    if (isDonationRoute) {
      setCurrentRouteType(ROUTE_TYPES.DONATION);
    }
  }, [isDonationRoute, setCurrentRouteType]);

  const isExceptRoute = currentRoute
    ? [
        ROUTES.MY_FAMILY_UPDATE,
        ROUTES.AUTH,
        ROUTES.CROSS_DOMAIN_AUTH,
        ROUTES.CROSS_DOMAIN_CALLBACK,
        ROUTES.SEARCH,
        ROUTES.ERROR,
        ROUTES.BENEFIT_NOTIFICATION,
        ROUTES.KB_PAY_FILTER,
        ROUTES.JOIN_COMMON,
        ROUTES.JOIN_COMPLETE,
        ROUTES.JOIN_USER_INFO,
      ]
        .map(({ id }) => id)
        .includes(currentRoute.id)
    : true;

  useEffect(() => {
    if (!pathname || isExceptRoute || !isKbWebview() || !isLogin) return;

    let kbDummyPathname = pathname;
    const params: { [key: string]: string } = {};

    searchParams?.forEach((value, key) => {
      if (key === SEARCH_PARAMS.POST_REDIRECT) return;

      const lastChar = kbDummyPathname.slice(-1);

      //! ⚠️ KB 쪽에서 SearchParams가 달라도 Pathname이 같으면 같은 페이지로 인식하기 때문에 Pathname에 파라미터를 붙여주는 로직 미들웨어에서 벗겨내는 로직으로 페이지 이동
      kbDummyPathname =
        kbDummyPathname +
        `${lastChar === '/' ? '' : '/'}${KB_DUMMY_PATHNAME}${KB_DUMMY_PATHNAME_SPLIT_POINT}${key}${KB_DUMMY_PATHNAME_SPLIT_POINT}${value}`;

      params[key] = value;
    });

    const url = new URL(kbDummyPathname, location.origin).toString();

    const { historyList, setHistoryList } = kbHistoryStore.getState();

    const lastHistory = historyList[historyList.length - 1];

    const isNewHistory = lastHistory !== url;

    if (!isNewHistory) return;

    const hasReplaceMarkKb =
      sessionStorage.getItem(STORAGE_KEY.REPLACE_MARK_KB) === 'true';
    if (hasReplaceMarkKb) {
      sessionStorage.removeItem(STORAGE_KEY.REPLACE_MARK_KB);
    }

    switch (pathname) {
      //* 게이트웨이 앱스킴을 통해 앱에 진입 한 경우 강제로 히스토리를 추가해줌. (이전 히스토리가 없기 때문에 뒤로가기 이슈로 인해 추가)
      case ROUTES.GATEWAY.pathname:
        {
          if (historyList.length > 0) return;

          const homeUrl = new URL(
            MODULE_ROUTES.KB_HOME.pathname,
            location.origin,
          ).toString();

          const donationHomeUrl = new URL(
            DONATION_ROUTES.HOME.pathname,
            location.origin,
          ).toString();

          moveBackInfo(homeUrl, {}, 'Y', 'addHistoryStackThisDonationHome');
          setHistoryList(() => [homeUrl, donationHomeUrl]);
        }

        break;
      default:
        moveBackInfo(url, params);
        setHistoryList((history) => [...history, url]);

        break;
    }
  }, [isExceptRoute, isLogin, pathname, searchParams]);

  return <></>;
};
